import type { ReactNode } from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import { AppShell } from "@/components/shell/app-shell";
import { TenantLayout } from "@/components/shell/tenant-layout";
import { AgentLayout } from "@/components/shell/agent-layout";
import { AuthGuard } from "@/features/auth/auth-guard";
import { useAuth } from "@/features/auth/auth-provider";

import { DashboardPage } from "@/features/dashboard/dashboard-page";
import { TenantActivityPage } from "@/features/tenants/tenant-activity-page";
import { RuntimeTargetsPage } from "@/features/runtime-targets/runtime-targets-page";
import { AiProvidersPage } from "@/features/integrations/ai-providers-page";
import { PluginsPage } from "@/features/plugins/plugins-page";
import { PluginDetailPage } from "@/features/plugins/plugin-detail-page";
import { PlatformSkillEditorPage, SkillEditorPage, SkillsPage } from "@/features/skills/skills-page";
import { AgentListPage } from "@/features/agents/agent-list-page";
import { ConversationsPage } from "@/features/conversations/conversations-page";
import { AdminConversationUserPickerPage } from "@/features/conversations/admin-conversation-user-picker-page";
import { AdminConversationsPage } from "@/features/conversations/admin-conversations-page";
import { OperatorConsolePage } from "@/features/operator-console/operator-console-page";
import { CreateAgentPage } from "@/features/agents/create-agent-page";
import { AgentOverviewTab } from "@/features/agents/agent-editor-page";
import { AgentPersonaTab } from "@/features/agents/agent-persona-tab";
import { AgentInstructionsTab } from "@/features/agents/agent-instructions-tab";
import { AgentIdentityTab } from "@/features/agents/agent-identity-tab";
import { AgentUserTab } from "@/features/agents/agent-user-tab";
import { AgentPolicyTab } from "@/features/agents/agent-policy-tab";
import { AgentAccessTab } from "@/features/agents/agent-access-tab";
import { AgentDeploymentsTab } from "@/features/agents/agent-deployments-tab";
import { AgentSkillsTab } from "@/features/agents/agent-skills-tab";
import { ChannelConnectionsPage } from "@/features/channels/channel-connections-page";
import { CreateConnectionPage } from "@/features/channels/create-connection-page";
import { ChannelDeploymentsPage } from "@/features/channels/channel-deployments-page";
import { CreateDeploymentPage } from "@/features/channels/create-deployment-page";
import { EditDeploymentPage } from "@/features/channels/edit-deployment-page";
import { ConnectorsPage as ConnectorInstallsPage } from "@/features/connectors/connectors-page";
import { CreateConnectorPage } from "@/features/connectors/create-connector-page";
import { CreateMcpToolsetPage } from "@/features/connectors/create-mcp-toolset-page";
import { ConnectorImplementationsPage } from "@/features/connectors/connector-implementations-page";
import {
  ConnectorPackageListPage,
  ConnectorPackageNewPage,
  ConnectorPackageWorkbenchPage,
} from "@/features/connector-packages/connector-package-workbench-page";
import { AutomationsPage } from "@/features/automations/automations-page";
import { AutomationInboxPage } from "@/features/automations/automation-inbox-page";
import { OutboundBroadcastsPage } from "@/features/outbound/outbound-broadcasts-page";
import { CreateAutomationPage } from "@/features/automations/create-automation-page";
import { EditAutomationPage } from "@/features/automations/edit-automation-page";
import { WorkflowPoliciesPage } from "@/features/workflows/workflow-policies-page";
import { CreateWorkflowPolicyPage } from "@/features/workflows/create-workflow-policy-page";
import { IdentityRulesPage } from "@/features/identity/identity-rules-page";
import { CreateIdentityRulePage } from "@/features/identity/create-identity-rule-page";
import { EditTenantPage } from "@/features/tenants/edit-tenant-page";
import { EditWorkflowPolicyPage } from "@/features/workflows/edit-workflow-policy-page";
import { EditIdentityRulePage } from "@/features/identity/edit-identity-rule-page";
import { EditConnectionPage } from "@/features/channels/edit-connection-page";
import { EditConnectorPage } from "@/features/connectors/edit-connector-page";
import { SnapshotsPage } from "@/features/snapshots/snapshots-page";
import { ExecutionListPage } from "@/features/executions/execution-list-page";
import { ExecutionDetailPage } from "@/features/executions/execution-detail-page";
import { AuditPage } from "@/features/audit/audit-page";
import { SettingsPage } from "@/features/settings/settings-page";

function RouteLoading() {
  return (
    <div
      className="flex items-center justify-center min-h-screen"
      style={{ background: "var(--bg)", color: "var(--muted-foreground)" }}
    >
      <p className="text-sm">Loading...</p>
    </div>
  );
}

function HomeRedirect() {
  const { status, tenant } = useAuth();
  if (status === "loading") {
    return <RouteLoading />;
  }
  if (tenant) {
    return <Navigate to="/workspace" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

function TenantScopedLayout() {
  const { status, tenant } = useAuth();

  if (status === "loading") {
    return <RouteLoading />;
  }
  if (!tenant) {
    return <Navigate to="/dashboard" replace />;
  }
  return <TenantLayout />;
}

function TenantScopedRuntimePage() {
  return (
    <TenantScopedPlatformPage>
      <RuntimeTargetsPage />
    </TenantScopedPlatformPage>
  );
}

function TenantScopedDeploymentsPage() {
  return (
    <TenantScopedPlatformPage fallbackPath="integrations/channels">
      <ChannelDeploymentsPage />
    </TenantScopedPlatformPage>
  );
}

function TenantScopedSnapshotsPage() {
  return (
    <TenantScopedPlatformPage>
      <SnapshotsPage />
    </TenantScopedPlatformPage>
  );
}

function TenantScopedPlatformPage({
  children,
  fallbackPath,
}: {
  children: ReactNode;
  fallbackPath?: string;
}) {
  const { status, tenant, isPlatformAdmin } = useAuth();

  if (status === "loading") {
    return <RouteLoading />;
  }
  if (!tenant) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!isPlatformAdmin) {
    return <Navigate to={`/workspace/${fallbackPath ?? ""}`.replace(/\/$/, "")} replace />;
  }
  return <>{children}</>;
}

function TenantScopedEditPage() {
  const { status, tenant } = useAuth();

  if (status === "loading") {
    return <RouteLoading />;
  }
  if (!tenant) {
    return <Navigate to="/dashboard" replace />;
  }
  return <EditTenantPage />;
}

const router = createBrowserRouter([
  {
    path: "/",
    element: <AuthGuard />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: "dashboard", element: <DashboardPage /> },
          { path: "workspace/edit", element: <TenantScopedEditPage /> },
          {
            path: "workspace",
            element: <TenantScopedLayout />,
            children: [
              { index: true, element: <DashboardPage /> },
              { path: "runtime", element: <TenantScopedRuntimePage /> },
              { path: "agents", element: <AgentListPage /> },
              { path: "skills", element: <SkillsPage /> },
              { path: "skills/new", element: <SkillEditorPage /> },
              {
                path: "skills/library/new",
                element: (
                  <TenantScopedPlatformPage fallbackPath="skills">
                    <PlatformSkillEditorPage />
                  </TenantScopedPlatformPage>
                ),
              },
              {
                path: "skills/library/:platformSkillId",
                element: (
                  <TenantScopedPlatformPage fallbackPath="skills">
                    <PlatformSkillEditorPage />
                  </TenantScopedPlatformPage>
                ),
              },
              { path: "skills/:tenantSkillId", element: <SkillEditorPage /> },
              { path: "conversations", element: <ConversationsPage /> },
              { path: "operator-console", element: <OperatorConsolePage /> },
              { path: "operator-console/:sessionId", element: <OperatorConsolePage /> },
              { path: "automation-inbox", element: <AutomationInboxPage /> },
              { path: "automation-inbox/:notificationId", element: <AutomationInboxPage /> },
              { path: "outbound", element: <OutboundBroadcastsPage /> },
              { path: "conversations/admin", element: <AdminConversationUserPickerPage /> },
              { path: "conversations/admin/:userId", element: <AdminConversationsPage /> },
              { path: "agents/new", element: <CreateAgentPage /> },
              { path: "plugins", element: <PluginsPage /> },
              { path: "plugins/:pluginId", element: <PluginDetailPage /> },
              { path: "ai-providers", element: <AiProvidersPage /> },
              {
                path: "agents/:agentId",
                element: <AgentLayout />,
                children: [
                  { index: true, element: <AgentOverviewTab /> },
                  { path: "persona", element: <AgentPersonaTab /> },
                  { path: "instructions", element: <AgentInstructionsTab /> },
                  { path: "identity", element: <AgentIdentityTab /> },
                  { path: "user", element: <AgentUserTab /> },
                  { path: "policy", element: <AgentPolicyTab /> },
                  { path: "skills", element: <AgentSkillsTab /> },
                  { path: "overlays", element: <Navigate to=".." replace /> },
                  { path: "access", element: <AgentAccessTab /> },
                  { path: "deployments", element: <AgentDeploymentsTab /> },
                ],
              },
              { path: "channels", element: <ChannelConnectionsPage /> },
              { path: "channels/new", element: <CreateConnectionPage /> },
              { path: "channels/:connectionId", element: <EditConnectionPage /> },
              { path: "deployments", element: <TenantScopedDeploymentsPage /> },
              {
                path: "deployments/new",
                element: (
                  <TenantScopedPlatformPage fallbackPath="integrations/channels">
                    <CreateDeploymentPage />
                  </TenantScopedPlatformPage>
                ),
              },
              {
                path: "deployments/:deploymentId",
                element: (
                  <TenantScopedPlatformPage fallbackPath="integrations/channels">
                    <EditDeploymentPage />
                  </TenantScopedPlatformPage>
                ),
              },
              {
                path: "connectors",
                element: (
                  <TenantScopedPlatformPage fallbackPath="connector-installs">
                    <ConnectorPackageListPage />
                  </TenantScopedPlatformPage>
                ),
              },
              {
                path: "connectors/new",
                element: (
                  <TenantScopedPlatformPage fallbackPath="connector-installs">
                    <ConnectorPackageNewPage />
                  </TenantScopedPlatformPage>
                ),
              },
              {
                path: "connectors/:connectorPackageId",
                element: (
                  <TenantScopedPlatformPage fallbackPath="connector-installs">
                    <ConnectorPackageWorkbenchPage />
                  </TenantScopedPlatformPage>
                ),
              },
              { path: "connector-installs", element: <ConnectorInstallsPage /> },
              {
                path: "connector-packages",
                element: <Navigate to="/workspace/connectors" replace />,
              },
              { path: "connector-installs/new", element: <CreateConnectorPage /> },
              { path: "connector-installs/mcp/new", element: <CreateMcpToolsetPage /> },
              { path: "connector-installs/:connectorId", element: <EditConnectorPage /> },
              { path: "implementations", element: <ConnectorImplementationsPage /> },
              { path: "automations", element: <AutomationsPage /> },
              { path: "automations/new", element: <CreateAutomationPage /> },
              { path: "automations/:automationId", element: <EditAutomationPage /> },
              { path: "workflows", element: <WorkflowPoliciesPage /> },
              { path: "workflows/new", element: <CreateWorkflowPolicyPage /> },
              { path: "workflows/:workflowKey", element: <EditWorkflowPolicyPage /> },
              { path: "identity", element: <IdentityRulesPage /> },
              { path: "identity/new", element: <CreateIdentityRulePage /> },
              { path: "identity/:channel/:userId", element: <EditIdentityRulePage /> },
              { path: "snapshots", element: <TenantScopedSnapshotsPage /> },
              { path: "activity", element: <TenantActivityPage /> },
            ],
          },

          { path: "executions", element: <ExecutionListPage /> },
          { path: "executions/:executionId", element: <ExecutionDetailPage /> },
          { path: "audit", element: <AuditPage /> },
          { path: "settings", element: <SettingsPage /> },
        ],
      },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
